import {
  PROVIDER_ACCOUNT_NAMES,
  providerAuthExpectsPastedCode,
  supportsPersonalSubscription,
  type LocalApi,
  type ProviderCredentialCloudSyncInput,
  type ProviderAuthSession,
  type ProviderKind,
} from "@cmmd-forge/contracts";

import { cmmdApiClient } from "../cmmdApi";
import { createServerApi } from "../../localApi";
import type { WsRpcClient } from "../../rpc/wsRpcClient";
import {
  createAuthRuntimeRpcConnection,
  type AuthRuntimeRpcConnection,
} from "./authRuntimeConnection";

/**
 * Card 1359 — orchestrates a cloud-only personal-provider sign-in end to end.
 *
 * A control surface with no execution plane cannot run `codex login`, so this
 * provisions a throwaway auth VM, opens a lean relay connection to it, drives
 * the device-auth flow ON the VM (never the primary), surfaces the device
 * URL/code for the user, and — win, lose, or abort — tears the VM down. The
 * credential harvest itself happens server-side on the VM (owner-authenticated),
 * so this client never touches the credential; it only coordinates + cleans up.
 */
export type CloudProviderSignInPhase =
  | { readonly kind: "provisioning" }
  | { readonly kind: "connecting" }
  | {
      readonly kind: "awaiting_user";
      readonly deviceUrl: string | null;
      readonly userCode: string | null;
      /**
       * True for the authorize-URL + paste-code shape (Claude), whose CLI
       * blocks on stdin until the code the browser handed back is written to
       * it. False for Codex, which polls the provider for its own device code.
       *
       * Kept separate from `submitCode` because the shape does not change when
       * submission is temporarily locked — conflating them made a verifying
       * Claude session render Codex's "enter the code" instruction.
       */
      readonly expectsPastedCode: boolean;
      /**
       * Non-null only while a code may still be submitted. Goes null once one
       * is accepted: each submit writes a line to the CLI's stdin.
       */
      readonly submitCode: ((code: string) => Promise<void>) | null;
      /**
       * Set when a submit failed without us being able to tell whether the code
       * reached the CLI. Lives on the phase, not inside the form, because
       * committing unmounts the form — a form-local error would vanish at the
       * moment there is finally something to say.
       */
      readonly submitError: string | null;
    }
  | { readonly kind: "finishing" }
  | { readonly kind: "done" }
  | { readonly kind: "canceled" }
  | { readonly kind: "error"; readonly message: string };

export interface CloudProviderSignInResult {
  readonly status: "done" | "canceled" | "error";
  readonly message?: string;
}

type SignInServerApi = Pick<
  LocalApi["server"],
  | "startProviderAuth"
  | "getProviderAuthSession"
  // Claude's CLI blocks on stdin for the code the browser hands back; without
  // this the flow can only ever time out for that provider. See
  // providerAuthExpectsPastedCode.
  | "submitProviderAuthCode"
  | "syncPersonalCodexToCloud"
  | "syncPersonalClaudeToCloud"
>;

export interface CloudProviderSignInDeps {
  readonly provisionAuthRuntime: typeof cmmdApiClient.provisionAuthRuntime;
  readonly getAuthRuntimeState: typeof cmmdApiClient.getAuthRuntimeState;
  readonly connectAuthRuntime: typeof cmmdApiClient.connectAuthRuntime;
  readonly destroyAuthRuntime: typeof cmmdApiClient.destroyAuthRuntime;
  readonly createConnection: typeof createAuthRuntimeRpcConnection;
  readonly createServerApi: (client: WsRpcClient) => SignInServerApi;
  readonly sleep: (ms: number) => Promise<void>;
}

// Lazy, NOT a module-level const: referencing `createServerApi`/`cmmdApiClient`
// at module-eval time would trip existing tests that mock `../../localApi` with a
// curated export list (their mock predates `createServerApi`). Building the deps
// only when the flow actually runs keeps import of this module side-effect-free.
function defaultDeps(): CloudProviderSignInDeps {
  return {
    provisionAuthRuntime: () => cmmdApiClient.provisionAuthRuntime(),
    getAuthRuntimeState: () => cmmdApiClient.getAuthRuntimeState(),
    connectAuthRuntime: () => cmmdApiClient.connectAuthRuntime(),
    destroyAuthRuntime: () => cmmdApiClient.destroyAuthRuntime(),
    createConnection: createAuthRuntimeRpcConnection,
    createServerApi,
    sleep: (ms) => new Promise((resolve) => setTimeout(resolve, ms)),
  };
}

// Connect-to-wake budget. The auth VM runs on the Sprites substrate: no golden
// image, so it cold self-provisions (base env + git clone + `bun install` +
// server start), it emits NO readiness heartbeat, and it SUSPENDS when idle —
// there is nothing to poll for "ready" (the server only ever reaches "starting"
// on the control plane; observed in prod 2026-07-24). Instead we CONNECT through
// the relay, which wakes the sprite on inbound traffic and lets it finish
// booting, and retry the first device-auth RPC until the server is listening.
// Both connectAuthRuntime and resolveReplay authorize on the provisioned sprite
// alone (no running-status gate), so the WS reaches the edge and wakes it. This
// budget covers a cold sprite create (~2 min typical; margin to ~4).
const CONNECT_POLL_INTERVAL_MS = 3_000;
const CONNECT_MAX_POLLS = 80; // ~4 minutes (80 * 3s)
const SESSION_POLL_INTERVAL_MS = 1_000;
const SESSION_MAX_POLLS = 600; // ~10 minutes for the user to complete login
// Accepting a code restarts the clock with its own, shorter budget. The base
// budget measures how long a HUMAN may take to go authorize in a browser; once
// they have pasted a code back, the remaining wait is the CLI verifying it. A
// paste in the last poll interval used to be killed by base-budget exhaustion —
// reported as "did not complete in time" and the VM destroyed — while the CLI
// was still working on a code it had already accepted.
const VERIFY_MAX_POLLS = 120; // ~2 minutes for the CLI to verify

function isTerminal(session: ProviderAuthSession): boolean {
  return (
    session.state === "completed" ||
    session.state === "failed" ||
    session.state === "canceled" ||
    session.state === "expired"
  );
}

class SignInAborted extends Error {}

type EstablishedConnection = {
  readonly connection: AuthRuntimeRpcConnection;
  readonly server: SignInServerApi;
  readonly session: ProviderAuthSession;
};

/**
 * Connect-to-wake: open the relay connection to the auth VM and retry the first
 * device-auth RPC until it lands.
 *
 * On the Sprites substrate the auth VM has no golden image and no readiness
 * heartbeat, and it SUSPENDS when idle — so there is no "running" status to wait
 * for (it stays "starting" forever). connectAuthRuntime and the relay's
 * resolveReplay both authorize on the provisioned sprite alone (operational
 * Sprite name / dual-read identity set), with no running-status gate, so the WS
 * reaches the sprite edge and WAKES it. While the sprite finishes bootstrapping
 * the RPC fails; we retry (the transport re-acquires a fresh descriptor and
 * reconnects each attempt) until the server is listening or the budget elapses.
 * A fatal provisioning errorCode short-circuits the wait.
 */
async function establishAndStartDeviceAuth(
  deps: CloudProviderSignInDeps,
  runtimeId: string,
  provider: ProviderKind,
  throwIfAborted: () => void,
): Promise<{ ok: true; value: EstablishedConnection } | { ok: false; message: string }> {
  const initialDescriptor = await deps.connectAuthRuntime();
  const connection = deps.createConnection({ runtimeId, initialDescriptor });
  const server = deps.createServerApi(connection.client);
  try {
    let lastError = "the sign-in runtime did not become reachable in time.";
    for (let poll = 0; poll < CONNECT_MAX_POLLS; poll += 1) {
      throwIfAborted();
      // A fatal provisioning failure is terminal — stop waking/retrying.
      const state = await deps.getAuthRuntimeState().catch(() => null);
      if (state?.runtime?.errorCode) {
        await connection.dispose().catch(() => undefined);
        return { ok: false, message: `Auth runtime failed: ${state.runtime.errorCode}` };
      }
      try {
        // The first RPC to reach the VM: this WS traffic wakes the sprite and,
        // once its server is listening, starts device-auth. Until then it throws
        // and we retry (the transport reconnects with a fresh descriptor).
        const session = await server.startProviderAuth({ provider });
        return { ok: true, value: { connection, server, session } };
      } catch (error) {
        throwIfAborted();
        lastError = error instanceof Error ? error.message : String(error);
      }
      await deps.sleep(CONNECT_POLL_INTERVAL_MS);
    }
    await connection.dispose().catch(() => undefined);
    return {
      ok: false,
      message: `Timed out connecting to the sign-in runtime (${lastError}).`,
    };
  } catch (error) {
    // Abort (or any unexpected throw) after the connection was opened: dispose it
    // here so a canceled sign-in never leaks the transport, then rethrow.
    await connection.dispose().catch(() => undefined);
    throw error;
  }
}

/** Poll an already-started device-auth session until it reaches a terminal state. */
async function pollDeviceAuthSession(
  deps: CloudProviderSignInDeps,
  server: SignInServerApi,
  started: ProviderAuthSession,
  onPhase: (phase: CloudProviderSignInPhase) => void,
  throwIfAborted: () => void,
): Promise<ProviderAuthSession> {
  let session = started;
  // One code per session, permanently. Every submit writes a line to the CLI's
  // stdin, and after a code is accepted the session STAYS in
  // `waiting_for_user` — so the box must close or a second code reaches a CLI
  // that already has one.
  //
  // This is deliberately not "unlock when the session changes": the coordinator
  // re-runs `interpretOutput` over ACCUMULATED output on every stdout/stderr
  // chunk, and ClaudeAuthStrategy keeps returning `waiting_for_user` with the
  // same authorize URL for as long as that URL sits anywhere in the buffer. So
  // ordinary CLI chatter refreshes `updatedAt` (and restores the "paste the
  // code" message) without the CLI having asked for anything — there is no
  // re-prompt signal to detect. A wrong code makes the CLI exit non-zero, which
  // surfaces as a failed session; the user then starts a fresh sign-in.
  let codeAccepted = false;
  // The poll loop never awaits a UI-triggered submit, so that RPC can resolve
  // after the flow has already settled. Emitting from it then would resurrect a
  // dead awaiting view over the final error.
  let settled = false;
  // Mutable so an accepted code can extend the deadline from where it landed.
  let pollsTaken = 0;
  let maxPolls = SESSION_MAX_POLLS;
  // Surfaced on the phase because committing unmounts the form that would
  // otherwise own it.
  let submitError: string | null = null;

  const emit = (current: ProviderAuthSession): void => {
    if (settled || isTerminal(current)) return;
    onPhase({
      kind: "awaiting_user",
      deviceUrl: current.deviceUrl ?? null,
      userCode: current.userCode ?? null,
      expectsPastedCode: providerAuthExpectsPastedCode(current),
      submitError,
      submitCode:
        providerAuthExpectsPastedCode(current) && !codeAccepted
          ? async (code: string) => {
              // Committing is the point of no return, so both the lock and the
              // budget are taken HERE rather than when the RPC returns.
              //
              // The lock, because a rejection does not prove the code missed
              // the CLI: ProviderAuthCoordinator.submitCode writes to stdin
              // before it builds a response, and the relay classifies this
              // method as `reject-uncertain` (NON_REPEATABLE_CREATE_METHODS)
              // for exactly that reason. Re-offering the box on failure could
              // write a second line into a CLI that already took the first.
              //
              // The budget, because the poll loop never awaits this request —
              // a slow relay would otherwise let the deadline lapse and tear
              // the VM down while the code was still in flight.
              //
              // So a failed submit is reconciled by polling: the CLI either
              // completes, or the session ends and the flow reports it.
              //
              // The guard is HERE, inside the closure, because `codeAccepted`
              // only suppresses callbacks in phases emitted later — it cannot
              // invalidate the callback the UI is already holding. On an
              // ambiguous rejection the form clears its busy state and keeps
              // that original reference bound to its button, so without this a
              // second press would write again.
              if (codeAccepted) return;
              codeAccepted = true;
              // REPLACE the human deadline, don't extend past it. The base
              // budget measures how long someone may take to go authorize in a
              // browser; once they've pasted, that allowance is spent and only
              // the CLI's verification remains. Keeping the larger of the two
              // held the VM for up to ten more minutes on a hung CLI.
              maxPolls = pollsTaken + VERIFY_MAX_POLLS;
              // Close the box in the UI now rather than at the next poll tick,
              // so the affordance disappears the moment the user commits.
              emit(current);
              let next: ProviderAuthSession;
              try {
                next = await server.submitProviderAuthCode({
                  sessionId: current.id,
                  code,
                });
              } catch (error) {
                // Report through the PHASE, not by rethrowing: committing has
                // already unmounted the form, so a form-local error would be
                // discarded exactly when there is something worth saying. The
                // lock stands — delivery is uncertain, not known-failed.
                submitError =
                  error instanceof Error && error.message
                    ? `Couldn't confirm your code reached the sign-in runtime (${error.message}). Waiting to see if it went through.`
                    : "Couldn't confirm your code reached the sign-in runtime. Waiting to see if it went through.";
                emit(current);
                return;
              }
              // Re-render immediately so the box closes on accept rather than
              // waiting for the next poll tick. `emit` drops this when the
              // session came back terminal or the flow already settled.
              emit(next);
            }
          : null,
    });
  };

  try {
    emit(session);
    while (pollsTaken < maxPolls && !isTerminal(session)) {
      await deps.sleep(SESSION_POLL_INTERVAL_MS);
      throwIfAborted();
      pollsTaken += 1;
      session = await server.getProviderAuthSession({ sessionId: session.id });
      emit(session);
    }
    return session;
  } finally {
    settled = true;
  }
}

/** Map a terminal session to a result, harvesting the credential on success. */
async function resolveCompletedOutcome(
  server: SignInServerApi,
  session: ProviderAuthSession,
  onPhase: (phase: CloudProviderSignInPhase) => void,
  syncInput: ProviderCredentialCloudSyncInput,
): Promise<CloudProviderSignInResult> {
  onPhase({ kind: "finishing" });
  // Owner-authenticated harvest (path A / #1849), never the G3-blocked grant write.
  // Pick the provider's harvest — codex reads ~/.codex/auth.json, claude reads
  // ~/.claude/.credentials.json; both deposit to the user store server-side.
  if (!supportsPersonalSubscription(session.provider)) {
    return fail(
      onPhase,
      `Sign-in completed but Forge has no credential harvest for ${PROVIDER_ACCOUNT_NAMES[session.provider]}.`,
    );
  }
  const harvest =
    session.provider === "claudeAgent"
      ? await server.syncPersonalClaudeToCloud(syncInput)
      : await server.syncPersonalCodexToCloud(syncInput);
  if (harvest.status !== "synced") {
    return fail(
      onPhase,
      harvest.message ?? `Sign-in succeeded but the credential did not sync (${harvest.status}).`,
    );
  }
  onPhase({ kind: "done" });
  return { status: "done" };
}

async function resolveOutcome(
  server: SignInServerApi,
  session: ProviderAuthSession,
  onPhase: (phase: CloudProviderSignInPhase) => void,
  syncInput: ProviderCredentialCloudSyncInput,
): Promise<CloudProviderSignInResult> {
  if (session.state === "completed") {
    return resolveCompletedOutcome(server, session, onPhase, syncInput);
  }
  if (session.state === "failed" || session.state === "expired") {
    return fail(onPhase, session.message ?? `Sign-in ${session.state}.`);
  }
  if (session.state === "canceled") {
    onPhase({ kind: "canceled" });
    return { status: "canceled" };
  }
  return fail(onPhase, "Sign-in did not complete in time.");
}

/**
 * Runs the full flow. Resolves with the outcome; never throws for expected
 * failure/cancel paths. `signal` aborting triggers teardown and a `canceled`
 * result. Machine provisioning + the live Fly boot are the one surface that
 * cannot be verified without live infra — everything else here is deterministic.
 */
/**
 * Only one cloud sign-in may be in flight per tab.
 *
 * The CMMD auth runtime is a per-user SINGLETON and `destroyAuthRuntime()`
 * takes no id — it tears down whatever runtime the user currently has. With
 * both the Codex and Claude flows enabled, onboarding renders a controller for
 * each, so two runs could overlap and either one's teardown would take out the
 * VM the other was still using.
 */
let activeSignIn: Promise<CloudProviderSignInResult> | null = null;

/**
 * Cross-TAB, not just cross-call. Two tabs on the same account each get their
 * own module scope, so a module-local promise never sees the other one; the Web
 * Locks API is per-origin and does. `ifAvailable` makes this fail fast — the
 * second attempt is refused rather than queued behind a sign-in the user may
 * never finish.
 */
const SIGN_IN_LOCK_NAME = "forge:cloud-provider-sign-in";

type LockManagerLike = {
  readonly request: (
    name: string,
    options: { readonly ifAvailable: true },
    callback: (lock: unknown) => Promise<CloudProviderSignInResult>,
  ) => Promise<CloudProviderSignInResult>;
};

function readLockManager(): LockManagerLike | null {
  const locks = (globalThis as { navigator?: { locks?: unknown } }).navigator?.locks;
  return typeof (locks as LockManagerLike | undefined)?.request === "function"
    ? (locks as LockManagerLike)
    : null;
}

export async function runCloudProviderSignIn(input: {
  readonly provider: ProviderKind;
  readonly credentialTarget?: ProviderCredentialCloudSyncInput["target"];
  readonly onPhase: (phase: CloudProviderSignInPhase) => void;
  readonly signal?: AbortSignal;
  readonly deps?: Partial<CloudProviderSignInDeps>;
}): Promise<CloudProviderSignInResult> {
  // Refuse BEFORE provisioning either way: a run that provisions and then
  // unwinds would destroy the singleton out from under the run already using it.
  const busy = (): CloudProviderSignInResult =>
    fail(input.onPhase, "Another provider sign-in is already running. Finish or cancel it first.");

  const locks = readLockManager();
  if (locks) {
    return locks.request(SIGN_IN_LOCK_NAME, { ifAvailable: true }, async (lock) =>
      lock ? runCloudProviderSignInExclusive(input) : busy(),
    );
  }

  // No Web Locks (older browser, or a test realm): fall back to same-realm
  // exclusion, which is strictly weaker but still prevents the common case.
  if (activeSignIn !== null) {
    return busy();
  }
  const run = runCloudProviderSignInExclusive(input);
  activeSignIn = run;
  try {
    return await run;
  } finally {
    activeSignIn = null;
  }
}

async function runCloudProviderSignInExclusive(input: {
  readonly provider: ProviderKind;
  readonly credentialTarget?: ProviderCredentialCloudSyncInput["target"];
  readonly onPhase: (phase: CloudProviderSignInPhase) => void;
  readonly signal?: AbortSignal;
  readonly deps?: Partial<CloudProviderSignInDeps>;
}): Promise<CloudProviderSignInResult> {
  const deps: CloudProviderSignInDeps = { ...defaultDeps(), ...input.deps };
  const { onPhase, signal } = input;

  const throwIfAborted = () => {
    if (signal?.aborted) throw new SignInAborted();
  };

  let connection: AuthRuntimeRpcConnection | null = null;

  try {
    // 1. Provision the throwaway auth Sprite. Non-blocking: CMMD creates the
    // sprite and returns as soon as operational Sprite identity is set; the
    // guest bootstraps in the background and is NOT reachable yet.
    onPhase({ kind: "provisioning" });
    throwIfAborted();
    const provisioned = await deps.provisionAuthRuntime();
    // Defense-in-depth: a well-formed response always carries `.runtime`. Guard
    // against a malformed/empty body so we surface a clear message instead of a
    // raw "cannot read properties of undefined (reading runtime)" TypeError.
    if (!provisioned?.runtime) {
      return fail(
        onPhase,
        "Sign-in is temporarily unavailable — the sign-in runtime could not be provisioned.",
      );
    }
    // A provision that already reports a fatal errorCode will never come up — fail
    // fast rather than spend the whole connect-to-wake budget on a dead sprite.
    if (provisioned.runtime.errorCode) {
      return fail(onPhase, `Auth runtime failed: ${provisioned.runtime.errorCode}`);
    }

    // 2. Connect-to-wake + start device-auth. The Sprites auth VM has no readiness
    // heartbeat and suspends when idle, so we do NOT wait for a "running" status
    // (it never comes). We connect through the relay — which wakes the sprite and
    // lets it finish booting — and retry until the VM is reachable and device-auth
    // has started ON the VM. See establishAndStartDeviceAuth.
    onPhase({ kind: "connecting" });
    const established = await establishAndStartDeviceAuth(
      deps,
      provisioned.runtime.runtimeId,
      input.provider,
      throwIfAborted,
    );
    if (!established.ok) return fail(onPhase, established.message);
    connection = established.value.connection;

    // 3. Poll the started session to a terminal state, then 4. resolve + harvest
    // owner-authenticated ON the VM. The VM is torn down in `finally`, so the
    // harvest must finish before the drop.
    const session = await pollDeviceAuthSession(
      deps,
      established.value.server,
      established.value.session,
      onPhase,
      throwIfAborted,
    );
    const syncInput = input.credentialTarget === "linked" ? ({ target: "linked" } as const) : {};
    return await resolveOutcome(established.value.server, session, onPhase, syncInput);
  } catch (error) {
    if (error instanceof SignInAborted) {
      onPhase({ kind: "canceled" });
      return { status: "canceled" };
    }
    return fail(onPhase, error instanceof Error ? error.message : String(error));
  } finally {
    // Single-use VM: always tear it down and drop the connection, on every path.
    await connection?.dispose().catch(() => undefined);
    await deps.destroyAuthRuntime().catch(() => undefined);
  }
}

function fail(
  onPhase: (phase: CloudProviderSignInPhase) => void,
  message: string,
): CloudProviderSignInResult {
  onPhase({ kind: "error", message });
  return { status: "error", message };
}
